import { computeCameraState } from "./cameraChoreography";

export const MAX_PREFETCH_TILES = 96;
const BATCH_SIZE = 6;
const prefetchedCache = new Set();

export const _clearPrefetchCache = () => {
  prefetchedCache.clear();
};

export const lonLatToTileXY = (lon, lat, z) => {
  const zoom = Math.max(0, Math.min(18, Math.round(z)));
  const n = Math.pow(2, zoom);
  const clampedLon = Math.max(-180, Math.min(180, lon));
  const clampedLat = Math.max(-85.05112878, Math.min(85.05112878, lat));

  const x = Math.floor(((clampedLon + 180) / 360) * n);
  const latRad = (clampedLat * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
  );

  return {
    x: Math.max(0, Math.min(n - 1, x)),
    y: Math.max(0, Math.min(n - 1, y)),
    z: zoom,
  };
};

export const buildTileUrl = (templateUrl, z, x, y) => {
  if (!templateUrl || typeof templateUrl !== "string") return "";
  return templateUrl
    .replace(/{z}/g, String(z))
    .replace(/{x}/g, String(x))
    .replace(/{y}/g, String(y))
    .replace(/\{a-c\}/g, "a")
    .replace(/\{a-d\}/g, "a")
    .replace(/\{s\}/g, "a");
};

export const sampleTrajectoryPoints = (waypoints, baseZoom = 8, routeStyle = "flight") => {
  if (!Array.isArray(waypoints) || waypoints.length === 0) return [];
  const points = [];

  waypoints.forEach((wp) => {
    if (Array.isArray(wp?.coordinate) && wp.coordinate.length >= 2) {
      points.push({
        lon: wp.coordinate[0],
        lat: wp.coordinate[1],
        zoom: wp.zoom || baseZoom,
      });
    }
  });

  const progressSteps = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0];
  progressSteps.forEach((progress) => {
    const cam = computeCameraState({
      waypoints,
      progress,
      baseZoom,
      routeStyle,
    });
    if (cam?.centerLonLat) {
      points.push({
        lon: cam.centerLonLat[0],
        lat: cam.centerLonLat[1],
        zoom: cam.zoom || baseZoom,
      });
    }
  });

  return points;
};

const collectTilesForPoint = (tileUrl, point, urlSet, maxTiles) => {
  const baseZ = Math.max(1, Math.min(18, Math.round(point.zoom)));
  const zooms = baseZ > 1 ? [baseZ, baseZ - 1] : [baseZ];

  for (const z of zooms) {
    if (urlSet.size >= maxTiles) break;
    const n = Math.pow(2, z);
    const { x: cx, y: cy } = lonLatToTileXY(point.lon, point.lat, z);

    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        if (urlSet.size >= maxTiles) break;
        const nx = Math.max(0, Math.min(n - 1, cx + dx));
        const ny = Math.max(0, Math.min(n - 1, cy + dy));
        const url = buildTileUrl(tileUrl, z, nx, ny);
        if (url) urlSet.add(url);
      }
    }
  }
};

export const computeRouteTileUrls = ({
  tileUrl,
  waypoints = [],
  baseZoom = 8,
  routeStyle = "flight",
  maxTiles = MAX_PREFETCH_TILES,
}) => {
  if (!tileUrl || !Array.isArray(waypoints) || waypoints.length === 0) {
    return [];
  }

  const points = sampleTrajectoryPoints(waypoints, baseZoom, routeStyle);
  const urlSet = new Set();

  for (const point of points) {
    if (urlSet.size >= maxTiles) break;
    collectTilesForPoint(tileUrl, point, urlSet, maxTiles);
  }

  return Array.from(urlSet).slice(0, maxTiles);
};

const loadSingleTileImage = (src) => {
  if (typeof Image === "undefined") return;
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.src = src;
  } catch {
    // Ignore image prefetch network or cross-origin failures in background
  }
};

const processBatchQueue = (queue) => {
  if (queue.length === 0) return;
  const currentBatch = queue.splice(0, BATCH_SIZE);
  currentBatch.forEach(loadSingleTileImage);

  if (queue.length > 0) {
    const scheduleNext = typeof requestIdleCallback === "function" ? requestIdleCallback : (cb) => setTimeout(cb, 40);
    scheduleNext(() => processBatchQueue(queue));
  }
};

export const prefetchRouteTiles = ({
  tileUrls = [],
  waypoints = [],
  baseZoom = 8,
  routeStyle = "flight",
  maxTiles = MAX_PREFETCH_TILES,
}) => {
  const templates = (Array.isArray(tileUrls) ? tileUrls : [tileUrls]).filter(
    (u) => typeof u === "string" && u.trim().length > 0
  );
  if (templates.length === 0 || !Array.isArray(waypoints) || waypoints.length === 0) {
    return [];
  }

  const uncachedQueue = [];
  templates.forEach((templateUrl) => {
    const computed = computeRouteTileUrls({
      tileUrl: templateUrl,
      waypoints,
      baseZoom,
      routeStyle,
      maxTiles,
    });
    computed.forEach((url) => {
      if (!prefetchedCache.has(url)) {
        prefetchedCache.add(url);
        uncachedQueue.push(url);
      }
    });
  });

  processBatchQueue(uncachedQueue);
  return uncachedQueue;
};
